import axios from 'axios'

import { apiClient } from './client'

const formatLocalDateKey = (date: Date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

export type MeasurementUnit = 'Metric' | 'Imperial'
export type EnduranceFrequency = 'FourPlusDays' | 'TwoThreeDays' | 'OneDayOrLess' | 'Occasionally' | 'NotAtAll'
export type HikingFrequency = 'ThreePlusDays' | 'OneTwoDays' | 'OneThreeDaysMonth' | 'FewTimes' | 'NotAtAll'
export type TrueElevationWorkoutAccess = 'Stairs' | 'StepUps' | 'LocalHikesOrTrails' | 'Hills'
export type EquipmentAccessOption = 'TreadmillWithIncline' | 'StairMachine'
export type PackWeightOption = 'None' | 'Day' | 'Ultralight' | 'Lightweight' | 'Traditional' | 'Unknown' | 'Custom'
export type HikeConcern = 'Endurance' | 'Joints' | 'Altitude' | 'Strength' | 'Other'
export type OnboardingProgress =
  | 'NotStarted'
  | 'IntroSlidesSeen'
  | 'AccountCreated'
  | 'UsernameCompleted'
  | 'AgeCompleted'
  | 'HikingFrequencyCompleted'
  | 'EnduranceCompleted'
  | 'AvailabilityCompleted'
  | 'TrainingDaysCompleted'
  | 'EquipmentCompleted'
  | 'UnitsCompleted'
  | 'GoalSelected'
  | 'PackWeightCompleted'
  | 'HikeDateCompleted'
  | 'ConcernsCompleted'
  | 'PlanGenerated'
  | 'OnboardingCompleted'
  | 'GymAccessCompleted'
  | 'TrueElevationCompleted'
  | 'PreferredDaysCompleted'
  | 'SummaryReviewed'

export type AgeRange = 'Range18To29' | 'Range30To39' | 'Range40To49' | 'Range50Plus'
export type LoginType = 'EmailPassword' | 'Apple' | 'Google' | 'Facebook'

export type OnboardingData = {
  ageRange?: AgeRange | null
  trainingDaysPerWeek?: number | null
  preferredDays?: string[]
  enduranceFrequency?: EnduranceFrequency | null
  hikingFrequency?: HikingFrequency | null
  username?: string | null
  fullName?: string | null
  measurementUnit?: MeasurementUnit | null
  trueElevationWorkouts?: TrueElevationWorkoutAccess[]
  equipmentAccess?: EquipmentAccessOption[]
  packWeightOption?: PackWeightOption | null
  packWeightKg?: number | null
  hikeDate?: string | null
  hikeDateUnknown?: boolean | null
  concerns?: HikeConcern[]
  otherConcerns?: string | null
}

export type CoachNote = {
  createdAt: string
  reason: string
  message: string
  weekNumber?: number | null
  effectiveDate?: string | null
  rationale?: Record<string, unknown> | null
}

export type UsernameHistoryEntry = {
  previousUsername?: string | null
  newUsername?: string | null
  changedAt: string
}

export type MobileUser = {
  userId: string
  email?: string | null
  loginType?: LoginType | null
  username?: string | null
  fullName?: string | null
  profileImageUrl?: string | null
  createdAt: string
  lastLoginAt?: string | null
  onboardingData?: OnboardingData | null
  onboardingProgress?: OnboardingProgress | null
  onboardingProgressPercent?: number | null
  isSubscriber?: boolean | null
  subscriptionStoreOfferType?: 'Unknown' | 'Introductory' | 'Promotional' | 'OfferCode' | 'WinBack' | null
  subscriptionStoreOfferId?: string | null
  coachNotes?: CoachNote[]
  usernameHistory?: UsernameHistoryEntry[]
  usernameChangeAllowedAt?: string | null
}

export type UserActivityLogEntry = {
  id: string
  userId: string
  eventType: string
  source: string
  entityType?: string | null
  entityId?: string | null
  occurredAt: string
  receivedAt: string
  appVersion?: string | null
  platform?: string | null
  properties?: Record<string, unknown> | null
}

export type UserActivityLogResponse = {
  entries: UserActivityLogEntry[]
  skip: number
  limit: number
  hasMore: boolean
}

export type MobileUsersResponse = {
  users: MobileUser[]
  total: number
  filteredTotal: number
  paidTotal: number
  onboardingIncompleteTotal: number
  skip: number
  limit: number
}

export type MobileUsersSort = 'recent' | 'oldest' | 'progress' | 'paid' | 'free'

export type MobileUsersParams = {
  skip?: number
  limit?: number
  search?: string
  sortBy?: MobileUsersSort
  onboardingIncomplete?: boolean
}

export type MobileImpersonationLinkResponse = {
  ticket: string
  targetUserId: string
  expiresAt: string
  expiresInSeconds: number
}

export type PasswordResetResponse = {
  message: string
}

export type UpdateMobileUserRequest = {
  username?: string | null
  onboardingData?: OnboardingData | null
}

export type UpdateMobileUserIdentityRequest = {
  email: string
}

export type TrainingPlanTargets = {
  totalDistanceKm: number
  totalElevationGainMeters: number
  targetPackWeightKg: number
}

export type WorkoutExercise = {
  code: string
  name: string
  category: string
  order: number
  sets?: number | null
  reps?: number | null
  durationMinutes?: number | null
  distanceKm?: number | null
  elevationGainMeters?: number | null
  videoKey?: string | null
  thumbnailKey?: string | null
  usePack: boolean
  notes?: string | null
}

export type PlanGenerationTraceContext = {
  date?: string | null
  dayOfWeek?: string | null
  dayType?: string | null
  sessionType?: string | null
  reason?: string | null
  distanceKm?: number | null
  elevationGainMeters?: number | null
  packWeightKg?: number | null
  exercises: string[]
  summary: string
}

export type PlanGenerationTraceEntry = {
  weekNumber: number
  date?: string | null
  rule: string
  action: string
  detail: string
  contexts: PlanGenerationTraceContext[]
}

export type WorkoutDay = {
  id: string
  date: string
  dayOfWeek: string
  isMilestone: boolean
  milestoneNumber?: number | null
  milestoneFocus?: string | null
  milestoneHiddenFromUser: boolean
  milestoneUserChoice?: string | null
  isLongWorkout: boolean
  distanceKm: number
  elevationGainMeters: number
  packWeightKg: number
  elevationType: string
  exercises: WorkoutExercise[]
  estimatedDurationMinutes: number
  notes?: string | null
  completed: boolean
  completedAt?: string | null
  actualDistanceKm?: number | null
  actualElevationGainMeters?: number | null
  perceivedDifficulty?: string | null
  hadPain?: boolean | null
  hadFatigue?: boolean | null
  surveyThrottleMultiplier?: number | null
  generationTrace: PlanGenerationTraceEntry[]
}

export type TrainingWeekSummary = {
  weekNumber: number
  phase: string
  weekStartDate: string
  weekEndDate: string
  targetDistanceKm: number
  targetElevationGainMeters: number
  targetPackWeightKg: number
  actualDistanceKm?: number | null
  actualElevationGainMeters?: number | null
  workoutCount: number
  completedWorkoutCount: number
  flags: string[]
  revisionState: string
  coachNoteCount: number
  generationTraceCount: number
}

export type TrainingWeekDetail = {
  weekNumber: number
  phase: string
  weekStartDate: string
  weekEndDate: string
  targetDistanceKm: number
  targetElevationGainMeters: number
  targetPackWeightKg: number
  prescribedDistanceKm?: number | null
  prescribedElevationGainMeters?: number | null
  prescribedPackWeightKg?: number | null
  prescribedWeightedWorkouts?: number | null
  prescribedLungeWorkouts?: number | null
  prescribedBalanceWorkouts?: number | null
  prescribedHiitWorkouts?: number | null
  originalPrescribedDistanceKm?: number | null
  originalPrescribedElevationGainMeters?: number | null
  originalPrescribedPackWeightKg?: number | null
  originalPrescribedWeightedWorkouts?: number | null
  originalPrescribedLungeWorkouts?: number | null
  originalPrescribedBalanceWorkouts?: number | null
  originalPrescribedHiitWorkouts?: number | null
  actualDistanceKm?: number | null
  actualElevationGainMeters?: number | null
  intensityFactor: number
  flags: string[]
  revisionState: string
  workouts: WorkoutDay[]
  coachNotes: CoachNote[]
  generationTrace: PlanGenerationTraceEntry[]
}

export type TrainingPlan = {
  id: string
  goalName: string
  goalType: string
  algorithmVersion?: string
  isManual?: boolean
  isOpenEnded?: boolean
  planStartDate: string
  planEndDate: string
  targetHikeDate?: string | null
  durationWeeks: number
  currentWeek: number
  targets: TrainingPlanTargets
  weeks: TrainingWeekSummary[]
  coachNotes: CoachNote[]
  generationTrace: PlanGenerationTraceEntry[]
  createdAt: string
  updatedAt?: string | null
}

export async function getMobileUsers(params?: MobileUsersParams) {
  const { data } = await apiClient.get<MobileUsersResponse>('/api/admin/mobile-users', {
    params
  })
  return data
}

export async function exportMobileUsersCsv() {
  const { data } = await apiClient.get<Blob>('/api/admin/mobile-users/export', {
    responseType: 'blob'
  })
  return data
}

export async function getMobileUser(userId: string) {
  const { data } = await apiClient.get<MobileUser>(`/api/admin/mobile-users/${encodeURIComponent(userId)}`)
  return data
}

export async function getUserActivityLog(
  userId: string,
  params?: { skip?: number; limit?: number; eventType?: string }
) {
  const { data } = await apiClient.get<UserActivityLogResponse>(
    `/api/admin/mobile-users/${encodeURIComponent(userId)}/activity-log`,
    { params }
  )
  return data
}

export async function createMobileUserImpersonationLink(userId: string, reason: string) {
  const { data } = await apiClient.post<MobileImpersonationLinkResponse>(
    `/api/admin/mobile-users/${encodeURIComponent(userId)}/impersonation-link`,
    { reason }
  )
  return data
}

export async function updateMobileUser(userId: string, payload: UpdateMobileUserRequest) {
  const { data } = await apiClient.put<MobileUser>(`/api/admin/mobile-users/${encodeURIComponent(userId)}`, payload)
  return data
}

export type RegenerateMobileUserTrainingPlanRequest = {
  enduranceFrequency?: EnduranceFrequency | null
  hikingFrequency?: HikingFrequency | null
}

export async function regenerateMobileUserTrainingPlan(
  userId: string,
  payload: RegenerateMobileUserTrainingPlanRequest
) {
  const { data } = await apiClient.post<TrainingPlan>(
    `/api/admin/mobile-users/${encodeURIComponent(userId)}/training-plan/regenerate`,
    { ...payload, localDate: formatLocalDateKey() }
  )
  return data
}

export async function updateMobileUserIdentity(userId: string, payload: UpdateMobileUserIdentityRequest) {
  const { data } = await apiClient.put<MobileUser>(
    `/api/admin/mobile-users/${encodeURIComponent(userId)}/identity`,
    payload
  )
  return data
}

export async function resetMobileUser(userId: string) {
  await apiClient.post(`/api/admin/mobile-users/${encodeURIComponent(userId)}/reset`)
}

export type WorkoutProjectionReconciliationResult = {
  userId: string
  workoutsScanned: number
  legacyPlannerGhostCompletionsReset?: number
  projectionStatesAdded: number
  projectionStatesReset: number
  projectionAttemptsStarted: number
}

export async function reconcileMobileUserWorkoutProjections(userId: string) {
  const { data } = await apiClient.post<WorkoutProjectionReconciliationResult>(
    `/api/admin/mobile-users/${encodeURIComponent(userId)}/workouts/reconcile-projections`
  )
  return data
}

export async function sendMobileUserPasswordReset(userId: string) {
  const { data } = await apiClient.post<PasswordResetResponse>(
    `/api/admin/mobile-users/${encodeURIComponent(userId)}/password-reset`
  )
  return data
}

export async function setMobileUserPassword(userId: string, password: string) {
  const { data } = await apiClient.put<PasswordResetResponse>(
    `/api/admin/mobile-users/${encodeURIComponent(userId)}/password`,
    { password }
  )
  return data
}

export async function deleteMobileUser(userId: string) {
  await apiClient.delete(`/api/admin/mobile-users/${encodeURIComponent(userId)}`)
}

export async function resetUsernameCooldown(userId: string) {
  await apiClient.delete(`/api/admin/mobile-users/${encodeURIComponent(userId)}/username-cooldown`)
}

export async function uploadMobileUserProfileImage(userId: string, file: File) {
  const formData = new FormData()
  formData.append('file', file)

  const { data } = await apiClient.post<MobileUser>(
    `/api/admin/mobile-users/${encodeURIComponent(userId)}/profile-image/upload`,
    formData
  )
  return data
}

export async function setMobileUserDefaultProfilePicture(userId: string, defaultProfilePictureId: string) {
  const { data } = await apiClient.put<MobileUser>(
    `/api/admin/mobile-users/${encodeURIComponent(userId)}/profile-image/default`,
    { defaultProfilePictureId }
  )
  return data
}

export async function setMobileUserSubscription(userId: string, isPaid: boolean) {
  const { data } = await apiClient.put<{ isPaid: boolean }>(
    `/api/admin/mobile-users/${encodeURIComponent(userId)}/subscription`,
    { isPaid }
  )
  return data
}

export async function getTrainingPlan(userId: string): Promise<TrainingPlan | null> {
  try {
    const { data } = await apiClient.get<TrainingPlan>(
      `/api/admin/mobile-users/${encodeURIComponent(userId)}/training-plan`,
      { params: { localDate: formatLocalDateKey() } }
    )
    return data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

export async function getTrainingPlanWeek(userId: string, weekNumber: number): Promise<TrainingWeekDetail | null> {
  try {
    const { data } = await apiClient.get<TrainingWeekDetail>(
      `/api/admin/mobile-users/${encodeURIComponent(userId)}/training-plan/weeks/${weekNumber}`
    )
    return data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

export type AdminUserWorkoutSummary = {
  id: string
  exerciseCode: string
  exerciseName: string
  durationSeconds: number
  elevationMeters?: number | null
  distanceKm?: number | null
  packWeightKg?: number | null
  difficultyRating?: number | null
  completedAt: string
  isPublic: boolean
  hasGpsTrace: boolean
}

export type AdminUserWorkoutsResponse = {
  workouts: AdminUserWorkoutSummary[]
  skip: number
  limit: number
  hasMore: boolean
}

export type WorkoutGpsTraceSampleState = 'running' | 'paused'

export type WorkoutGpsTraceSample = {
  latitude: number
  longitude: number
  timestamp: number
  state: WorkoutGpsTraceSampleState | string
  accepted: boolean
  rejectedReason?: string | null
  accuracyMeters?: number | null
  altitudeMeters?: number | null
  speedMps?: number | null
  source: string
}

export type WorkoutGpsTrace = {
  workoutId: string
  userId: string
  recordedAt: string
  truncated: boolean
  samples: WorkoutGpsTraceSample[]
}

export async function getUserWorkouts(
  userId: string,
  params?: { skip?: number; limit?: number }
): Promise<AdminUserWorkoutsResponse> {
  const { data } = await apiClient.get<AdminUserWorkoutsResponse>(
    `/api/admin/mobile-users/${encodeURIComponent(userId)}/workouts`,
    { params }
  )
  return data
}

export async function getUserWorkoutGpsTrace(
  userId: string,
  workoutId: string
): Promise<WorkoutGpsTrace | null> {
  try {
    const { data } = await apiClient.get<WorkoutGpsTrace>(
      `/api/admin/mobile-users/${encodeURIComponent(userId)}/workouts/${encodeURIComponent(workoutId)}/gps-trace`
    )
    return data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}
